import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Tab, Typography } from '@mui/material';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useCommonTranslation } from 'c-translation';
import type { Displays_Tag } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { useEntityPage } from 'c-hooks';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import * as yup from 'yup';
import { useUpdateEntityData } from 'c-data';
import { useForm } from 'react-hook-form';
import { DisplayTagForm } from 'c-displays/Pages/DisplaysTagEdit/DisplayTagForm';
import ReactGA from 'react-ga';
import DeAllocations from 'c-displays/Components/DeAllocations';
import { OperationalTimings } from '../../Components/OperationalTimings';

const DisplaysTagEditPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const { entity, id } = useEntityPage<Displays_Tag>();
    const t = useCommonTranslation();
    const [value, setValue] = React.useState('1');
    const validationSchema = yup.object().shape({});
    const { update, getUpdatingById } = useUpdateEntityData('Displays_Tag');
    const { state, error } = useMemo(() => getUpdatingById(id), [getUpdatingById, id]);

    const defaultValues = useMemo(
        () => ({
            ...entity,
        }),
        [entity],
    );

    const handleFormSubmit = useCallback(
        data => {
            if (data.operational === -1) {
                const { operational, ...dataWithoutOperational } = data;
                update(id, dataWithoutOperational);
            } else {
                update(id, data);
            }
        },
        [id, update],
    );

    const { control, handleSubmit } = useForm({
        defaultValues,
    });
    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <TabContext value={value}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={handleChange}>
                        <Tab label={t('Modules.Displays.DisplaysTagEdit.tabs.details')} value="1" />
                        <Tab
                            label={t('Modules.Displays.DisplaysTagEdit.tabs.operationalTimingsTab')}
                            value="2"
                        />
                        <Tab label={t('Modules.Displays.deAllocation')} value="3" />
                    </TabList>
                </Box>
                <TabPanel value="1">
                    <Box pt={2}>
                        <Typography variant="h1" pb={2}>
                            {t('Modules.Displays.DisplaysTagEdit.description')}
                        </Typography>
                        <form onSubmit={handleSubmit(handleFormSubmit)}>
                            <DisplayTagForm control={control} state={state} error={error} id={id} />
                        </form>
                    </Box>
                </TabPanel>
                <TabPanel value="2">
                    <OperationalTimings
                        operationalTimings={entity.operationalTimes}
                        id={id}
                        entityName="Displays_Tag"
                    />
                </TabPanel>
                <TabPanel value="3">
                    <DeAllocations type="Displays_Tag" id={id} />
                </TabPanel>
            </TabContext>
        </Box>
    );
};

const setup: PageEntityConfig<Displays_Tag> = {
    id: 'DisplaysTagEditPage',
    component: DisplaysTagEditPage,
    systemSearch: {
        title: undefined,
        description: undefined,
        icon: undefined,
        type: 'entity',
        aliases: undefined,
        route: PostAuthRoutes.Displays.DisplaysTagEdit,
        saveAsRecentPage: true,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            {
                label: 'Modules.Displays.AdminHome.metaTitle',
                link: PostAuthRoutes.Displays.DisplaysAdminHome,
            },
            {
                label: 'Modules.Displays.Tags.metaTitle',
                link: PostAuthRoutes.Displays.TagsList,
            },
        ],
        entityName: 'Displays_Tag',
        defaultIncludes: ['operationalTimes', 'operationalTimeScreens'],
        nameGen: (entity, id) => entity.name,
        permissions: [PermissionName.Admin],
    },
};

export default setup;
